// Ch78 · the Leads page's funnel — where leads die.
//
// Mirrors backend/api/services/lead_flow_truth.lead_funnel 1:1. Six counts over
// ONE cohort (the real leads that arrived inside the window), each a strict
// subset of the one above it, so a percentage means "of the step above".
//
// A step whose ledger could not be read carries `count: null` and its reason.
// That is NOT a zero: on a funnel a zero reads as "everybody dropped out here",
// which is the opposite claim from "we could not look".

import { api } from "../lib/api";

export type LeadFunnelStepKey =
  | "arrived"
  | "claimed"
  | "converted"
  | "contacted"
  | "replied"
  | "engaged";

export interface LeadFunnelStep {
  key: LeadFunnelStepKey;
  label: string;
  definition_id: string;
  /** null = the ledger behind this step could not be read. Never zero-for-unknown. */
  count: number | null;
  unknown_reason: string | null;
  /**
   * The step this one is a SUBSET of — named, not inferred from list order.
   * `engaged` is measured against `converted`, not against `replied`: somebody
   * can tour after a phone call without ever writing back, and reading it as
   * "of the step above" made a true number look like a contradiction in
   * production (Replied 0 beside Engaged 4).
   */
  measured_against: LeadFunnelStepKey | null;
  /** null when either side is unknown, or when the named step is 0. */
  of_previous_pct: number | null;
  lost_from_previous: number | null;
}

export interface LeadFunnelLeak {
  from: LeadFunnelStepKey;
  from_label: string;
  to: LeadFunnelStepKey;
  to_label: string;
  lost: number;
  of_previous_pct: number | null;
}

export interface CommercialScorecard {
  definition_id: string;
  computed_at: string;
  window: { start: string; end: string; timezone: string; boundary: string };
  coverage: string;
  filters: string;
  metrics: { key: string; label: string; value: number | null; status: string; note: string }[];
  sources: { table: string; status: string; rows: number | null; earliest: string | null; latest: string | null; undated: number | null }[];
  inquiries: { lead_id: string; source: string; created_at: string; qualified_at: string; verdict: string; duplicate_ids: string[]; client_ids: string[]; first_operator_attempt: {
    table: string; id: string; client_id: string; actor_id: string; send_action_id: string;
    provider_sid: string; intent_at: string; transport_status: string; hours_after_inquiry: number;
  } | null }[];
}

export interface WorkObligationPreview {
  definition_id: string;
  computed_at: string;
  status: "ok" | "partial";
  read_only: boolean;
  send_enabled: boolean;
  cron_enabled: boolean;
  calendar: { timezone: string; start_hour: number; end_hour: number; weekdays: number[]; confirmed: boolean; note: string };
  scope: string;
  coverage: string;
  receipt_note: string;
  sources: CommercialScorecard["sources"];
  obligations: { key: string; source_table: string; source_id: string; client_id: string;
    owner_user_id: string | null; source_at: string | null; state: string; note: string;
    active_hours: number | null; task_source?: string | null; raw_due_at?: string | null; raw_due_date?: string | null; followup_evidence: { table: string; id: string; actor_id: string;
      send_action_id: string; provider_sid: string; intent_at: string; transport_status: string } | null }[];
  candidates: { kind: string; obligation_key: string; owner_user_id: string; threshold_active_hours: number; candidate_key: string }[];
  morning_summary: { eligible: boolean; reason: string; window: string; obligation_keys: string[]; candidate_key: string | null };
}

export interface LeadFunnel {
  work_obligations?: WorkObligationPreview;
  commercial_scorecard?: CommercialScorecard;
  window_days: number;
  computed_at: string;
  status: "ok" | "unknown";
  steps: LeadFunnelStep[];
  biggest_leak: LeadFunnelLeak | null;
  /** Conversions whose claim row was lost. null = could not look. */
  unlinked_conversions: number | null;
  caveats: string[];
}

/**
 * The shape check is not ceremony. Found by the browser gate, not by a unit
 * test: an endpoint that is not deployed yet, a proxy that answers `{}`, or an
 * older backend all hand back something that satisfies TypeScript at compile
 * time and has no `steps` at runtime — and the card then reads `steps[0]` and
 * takes the WHOLE Leads page down with it. A response we cannot read is a read
 * FAILURE, which this page already knows how to say out loud.
 */
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const text = (value: unknown) => typeof value === "string";
const count = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0;
const nullableText = (value: unknown) => value === null || text(value);
const nullableCount = (value: unknown) => value === null || count(value);
const texts = (value: unknown) => Array.isArray(value) && value.every(text);
const fields = (value: Record<string, unknown>, names: string[]) => names.every(name => text(value[name]));
const sourceShape = (value: unknown) => Array.isArray(value) && value.every(source => record(source) &&
  text(source.table) && ["ok", "unknown"].includes(source.status as string) && nullableCount(source.rows) &&
  nullableText(source.earliest) && nullableText(source.latest) && nullableCount(source.undated));
const evidenceShape = (value: unknown) => record(value) && fields(value,
  ["table", "id", "actor_id", "send_action_id", "provider_sid", "intent_at", "transport_status"]);

function scorecardShape(value: unknown): boolean {
  return record(value) && fields(value, ["definition_id", "computed_at", "coverage", "filters"]) &&
    record(value.window) && fields(value.window, ["start", "end", "timezone", "boundary"]) &&
    sourceShape(value.sources) && Array.isArray(value.metrics) && value.metrics.every(metric => record(metric) &&
      fields(metric, ["key", "label", "note"]) && (metric.status === "unknown" ? metric.value === null :
        metric.status === "ok" && count(metric.value))) &&
    Array.isArray(value.inquiries) && value.inquiries.every(inquiry => record(inquiry) &&
      fields(inquiry, ["lead_id", "source", "created_at", "qualified_at", "verdict"]) &&
      texts(inquiry.duplicate_ids) && texts(inquiry.client_ids) &&
      (inquiry.first_operator_attempt === null || (record(inquiry.first_operator_attempt) &&
        evidenceShape(inquiry.first_operator_attempt) && text(inquiry.first_operator_attempt.client_id) &&
        count(inquiry.first_operator_attempt.hours_after_inquiry))));
}

function workShape(value: unknown): boolean {
  if (!record(value) || !fields(value, ["definition_id", "computed_at", "scope", "coverage", "receipt_note"]) ||
      !["ok", "partial"].includes(value.status as string) || value.read_only !== true ||
      value.send_enabled !== false || value.cron_enabled !== false || !record(value.calendar) ||
      !fields(value.calendar, ["timezone", "note"]) || typeof value.calendar.confirmed !== "boolean" ||
      !count(value.calendar.start_hour) || !count(value.calendar.end_hour) ||
      value.calendar.start_hour >= value.calendar.end_hour || value.calendar.end_hour > 24 ||
      !Array.isArray(value.calendar.weekdays) || !value.calendar.weekdays.every(day =>
        typeof day === "number" && Number.isInteger(day) && day >= 0 && day <= 6) ||
      !sourceShape(value.sources) || !Array.isArray(value.obligations) || !Array.isArray(value.candidates) ||
      !record(value.morning_summary) || typeof value.morning_summary.eligible !== "boolean" ||
      !fields(value.morning_summary, ["reason", "window"]) || !texts(value.morning_summary.obligation_keys) ||
      !nullableText(value.morning_summary.candidate_key)) return false;
  return value.obligations.every(row => record(row) &&
    fields(row, ["key", "source_table", "source_id", "client_id", "state", "note"]) &&
    ["unknown", "task_due", "task_scheduled", "awaiting_operator_followup", "operator_followup_evidenced"].includes(row.state as string) &&
    nullableText(row.owner_user_id) && nullableText(row.source_at) && nullableCount(row.active_hours) &&
    (row.task_source === undefined || nullableText(row.task_source)) &&
    (row.raw_due_at === undefined || nullableText(row.raw_due_at)) &&
    (row.raw_due_date === undefined || nullableText(row.raw_due_date)) &&
    (row.followup_evidence === null || evidenceShape(row.followup_evidence))) &&
    value.candidates.every(candidate => record(candidate) &&
      fields(candidate, ["kind", "obligation_key", "owner_user_id", "candidate_key"]) &&
      [1, 6].includes(candidate.threshold_active_hours as number));
}

function assertFunnelShape(payload: unknown): LeadFunnel {
  const funnel = payload as Partial<LeadFunnel> | null;
  if (!funnel || !Array.isArray(funnel.steps) || funnel.steps.length === 0) {
    throw new Error("the funnel endpoint answered in a shape this page cannot read");
  }
  if (funnel.commercial_scorecard !== undefined && !scorecardShape(funnel.commercial_scorecard)) {
    throw new Error("the commercial scorecard answered in a shape this page cannot read");
  }
  if (funnel.work_obligations !== undefined && !workShape(funnel.work_obligations)) {
    throw new Error("the work obligation preview answered in a shape this page cannot read");
  }
  return funnel as LeadFunnel;
}

export async function fetchLeadFunnel(days: number): Promise<LeadFunnel> {
  return assertFunnelShape(
    // Ch83 · retries a restart rather than reporting one. This read is what
    // put "Couldn't reach the LocateNow API" on the funnel card in front of
    // Grant, minutes after a backend deploy.
    await api.get<LeadFunnel>(
      `/analytics/lead-funnel?days=${encodeURIComponent(String(days))}`,
      { retry: true },
    ),
  );
}

/** One sentence naming the biggest leak, or what stops us saying one. */
export function describeLeak(funnel: LeadFunnel): string {
  const leak = funnel.biggest_leak;
  if (!leak) {
    const refused = funnel.steps.find((s) => s.count === null);
    if (refused) return `No leak can be named — ${refused.unknown_reason}`;
    const arrived = funnel.steps[0]?.count ?? 0;
    if (arrived === 0) return "Nobody arrived in this window, so there is nothing to leak.";
    return "Nobody is dropping out — every step holds.";
  }
  const people = leak.lost === 1 ? "person" : "people";
  return `Biggest leak: ${leak.from_label.toLowerCase()} → ${leak.to_label.toLowerCase()}. ${leak.lost} ${people} got as far as ${leak.from_label.toLowerCase()} and no further.`;
}
